/**
 * GuideProviderFinder — ZIP → state deep-link into the GUIDE provider
 * directory. Used on the public /tools/guide-check eligible result
 * (basePath="/resources") and the signed-in /guide eligibility verdict
 * (basePath="/help-finder").
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { zipToState } from "@/lib/zipToState";
import { track } from "@/lib/analytics";
import { loadPrefill, savePrefill } from "@/lib/prefill";

export function GuideProviderFinder({ basePath = "/resources", source = "guide-check-provider-finder" }) {
    const [zip, setZip] = useState("");
    const [prefilled, setPrefilled] = useState(false);
    const st = zip.length === 5 ? zipToState(zip) : null;

    // Phase 4 data reuse — a ZIP entered anywhere (here, Resources hub) is
    // remembered and offered back instead of asking again.
    useEffect(() => {
        loadPrefill().then(p => {
            if (p.zip) {
                setZip(z => {
                    if (z) return z;
                    setPrefilled(true);
                    return p.zip;
                });
            }
        });
    }, []);

    useEffect(() => {
        if (zip.length === 5 && st) savePrefill({ zip, state: st });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zip]);

    return (
        <div className="ml-card p-4 md:p-5 mt-3 bg-white" data-testid="guide-provider-finder">
            <p className="font-medium text-[#1F2624] text-sm md:text-base">Find participating GUIDE providers near you</p>
            <p className="text-xs text-[#6E7A74] mt-1">Enter your ZIP code and we&apos;ll show GUIDE participants in your state, plus the official CMS directory.</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                    value={zip}
                    onChange={e => setZip(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
                    inputMode="numeric"
                    placeholder="ZIP code"
                    data-testid="guide-finder-zip"
                    className="w-full sm:w-36 px-3.5 py-2.5 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                />
                <Link
                    to={st ? `${basePath}?state=${st}&category=guide#guide-providers` : `${basePath}?category=guide#guide-providers`}
                    onClick={() => track("tool_completed", { tool: source, state: st || "unknown" })}
                    data-testid="guide-finder-go"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-teal text-white font-medium text-sm px-5 py-2.5 hover:bg-teal-dark transition-colors"
                >
                    {st ? `See GUIDE providers in ${st}` : "Browse GUIDE providers"} <ArrowRight size={14} strokeWidth={2} />
                </Link>
            </div>
            {zip.length === 5 && !st && (
                <p className="mt-2 text-xs text-[#6E7A74]" data-testid="guide-finder-nomatch">
                    We couldn&apos;t match that ZIP — you can still browse providers by state.
                </p>
            )}
            {prefilled && zip.length === 5 && st && (
                <p className="mt-2 text-xs text-[#6E7A74]" data-testid="guide-finder-prefilled">
                    Using your saved ZIP — change it above if you&apos;re looking somewhere else.
                </p>
            )}
        </div>
    );
}
